import { useState, useEffect, useRef } from 'react';
import { Node, Edge } from 'reactflow';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';

interface FlowPreviewModalProps {
  nodes: Node[];
  edges: Edge[];
  campaignType?: 'OUTBOUND' | 'INBOUND';
  onClose: () => void;
}

interface Message {
  id: string;
  type: 'bot' | 'user';
  content: string;
  timestamp: Date;
}

export function FlowPreviewModal({ nodes, edges, campaignType, onClose }: FlowPreviewModalProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState('');
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const flowStartedRef = useRef(false); // Evitar iniciar flow múltiplas vezes
  const processedNodesRef = useRef<Set<string>>(new Set()); // Rastrear nós já processados
  const waitingForTriggerRef = useRef(false); // Se está aguardando trigger (palavra-chave)
  const flowActiveRef = useRef(false); // Se o flow já foi iniciado
  const botStoppedRef = useRef(false); // Se o bot foi encerrado por comando
  const pendingTimeoutsRef = useRef<NodeJS.Timeout[]>([]); // Timeouts pendentes para cancelar
  const saveVariableAttemptsRef = useRef<Record<string, number>>({}); // Rastrear tentativas por nó saveVariable

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Evitar iniciar flow múltiplas vezes (React Strict Mode em dev)
    if (flowStartedRef.current) {
      console.log('⚠️ [Preview] Flow already started, skipping...');
      return;
    }
    
    console.log('🎬 [Preview] Component mounted');
    console.log('🎬 [Preview] Campaign type:', campaignType);
    console.log('🎬 [Preview] Nodes received:', nodes);
    console.log('🎬 [Preview] Edges received:', edges);
    
    flowStartedRef.current = true;
    processedNodesRef.current.clear(); // Limpar nós processados ao reiniciar
    botStoppedRef.current = false; // Resetar flag de parada
    pendingTimeoutsRef.current = []; // Limpar timeouts
    saveVariableAttemptsRef.current = {}; // Limpar tentativas de saveVariable
    
    // Verificar se é bot INBOUND que precisa aguardar trigger
    const triggerNode = nodes.find(n => n.data?.nodeType === 'trigger');
    const triggerConfig = triggerNode?.data?.config || {};
    const triggerType = triggerConfig.triggerType || 'ANY_MESSAGE';
    
    if (campaignType === 'INBOUND' && (triggerType === 'KEYWORD_EXACT' || triggerType === 'KEYWORD_CONTAINS')) {
      // Bot INBOUND com palavras-chave - aguardar trigger
      waitingForTriggerRef.current = true;
      addMessage('bot', '👋 Aguardando sua mensagem... Digite uma palavra-chave para iniciar.');
    } else {
      // OUTBOUND ou INBOUND sem palavras-chave - iniciar automaticamente
      startFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fechar emoji picker ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  const startFlow = () => {
    console.log('🚀 [Preview] Starting flow...');
    console.log('🚀 [Preview] Total nodes:', nodes.length);
    console.log('🚀 [Preview] Total edges:', edges.length);

    // Encontrar nó trigger
    const triggerNode = nodes.find(n => n.data?.nodeType === 'trigger');
    console.log('🚀 [Preview] Trigger node:', triggerNode);

    if (!triggerNode) {
      addMessage('bot', '⚠️ Nenhum nó Início encontrado no fluxo.');
      return;
    }

    // Encontrar próximo nó após o trigger
    const nextEdge = edges.find(e => e.source === triggerNode.id);
    console.log('🚀 [Preview] Next edge from trigger:', nextEdge);

    if (!nextEdge) {
      addMessage('bot', '⚠️ Início não está conectado a nenhum nó.');
      return;
    }

    const nextNode = nodes.find(n => n.id === nextEdge.target);
    console.log('🚀 [Preview] Next node:', nextNode);
    console.log('🚀 [Preview] Next node type:', nextNode?.data?.nodeType);

    if (nextNode) {
      flowActiveRef.current = true;
      processNode(nextNode);
    } else {
      addMessage('bot', '⚠️ Nó seguinte ao Início não encontrado.');
    }
  };

  const processNode = (node: Node) => {
    // Se o bot foi encerrado, não processar mais nada
    if (botStoppedRef.current) {
      console.log('🛑 [Preview] Bot stopped - ignoring node processing');
      return;
    }
    
    // Evitar processar o mesmo nó duas vezes
    if (processedNodesRef.current.has(node.id)) {
      console.log('⚠️ [Preview] Node already processed, skipping:', node.id);
      return;
    }
    
    console.log('📍 [Preview] Processing node:', node.id, 'type:', node.data?.nodeType);
    processedNodesRef.current.add(node.id);
    setCurrentNodeId(node.id);
    const nodeType = node.data?.nodeType;

    // Tipos de nó que enviam mensagem
    const messageNodeTypes = ['action', 'text', 'image', 'video', 'audio', 'document'];

    console.log('📍 [Preview] Node type:', nodeType, 'is message type?', messageNodeTypes.includes(nodeType));

    if (messageNodeTypes.includes(nodeType)) {
      // Enviar mensagem do bot
      let content = '';

      if (nodeType === 'text' || nodeType === 'action') {
        content = node.data.config?.content || node.data.config?.message || 'Mensagem não configurada';
      } else if (nodeType === 'image') {
        const caption = node.data.config?.caption || '';
        content = `📷 Imagem${caption ? ': ' + caption : ''}`;
      } else if (nodeType === 'video') {
        const caption = node.data.config?.caption || '';
        content = `🎥 Vídeo${caption ? ': ' + caption : ''}`;
      } else if (nodeType === 'audio') {
        content = '🎵 Áudio';
      } else if (nodeType === 'document') {
        const fileName = node.data.config?.fileName || 'documento';
        content = `📄 Documento: ${fileName}`;
      }

      console.log('💬 [Preview] Sending message:', content);
      console.log('💬 [Preview] Node config:', node.data.config);
      addMessage('bot', content);

      // Verificar próximo nó
      const nextEdge = edges.find(e => e.source === node.id);
      if (nextEdge) {
        const nextNode = nodes.find(n => n.id === nextEdge.target);
        if (nextNode?.data?.nodeType === 'condition') {
          // Atualizar para o nó de condição e aguardar resposta do usuário
          console.log('⏸️ [Preview] Next node is condition, updating currentNodeId and waiting for user response');
          setCurrentNodeId(nextNode.id);
          return;
        } else if (nextNode?.data?.nodeType === 'saveVariable') {
          // Enviar prompt e aguardar resposta
          const promptMessage = nextNode.data.config?.promptMessage || 'Por favor, digite a informação solicitada:';
          addMessage('bot', promptMessage);
          setCurrentNodeId(nextNode.id);
          return;
        } else if (nextNode?.data?.nodeType === 'stop') {
          // Fim do fluxo
          const timeout = setTimeout(() => {
            if (!botStoppedRef.current) {
              addMessage('bot', '✅ Conversa finalizada.');
            }
          }, 500);
          pendingTimeoutsRef.current.push(timeout);
          return;
        } else if (nextNode) {
          // Continuar automaticamente para próximo nó
          const timeout = setTimeout(() => {
            if (!botStoppedRef.current) {
              processNode(nextNode);
            }
          }, 500);
          pendingTimeoutsRef.current.push(timeout);
        }
      } else {
        // Fim do fluxo
        const timeout = setTimeout(() => {
          if (!botStoppedRef.current) {
            addMessage('bot', '✅ Fim do fluxo.');
            flowActiveRef.current = false;
          }
        }, 500);
        pendingTimeoutsRef.current.push(timeout);
      }
    } else if (nodeType === 'condition') {
      // Aguardar resposta do usuário
      return;
    } else if (nodeType === 'saveVariable') {
      // Enviar prompt e aguardar resposta
      const promptMessage = config?.promptMessage || 'Por favor, digite a informação solicitada:';
      addMessage('bot', promptMessage);
      // Aguardar resposta do usuário
      return;
    } else if (nodeType === 'stop') {
      // Fim do fluxo
      if (!botStoppedRef.current) {
        addMessage('bot', '✅ Conversa finalizada.');
        flowActiveRef.current = false;
      }
      return;
    } else if (nodeType === 'delay') {
      // Simular delay e continuar
      const delaySeconds = node.data.config?.value || node.data.config?.delaySeconds || 0;
      const nextEdge = edges.find(e => e.source === node.id);

      if (nextEdge) {
        const nextNode = nodes.find(n => n.id === nextEdge.target);
        if (nextNode) {
          const timeout = setTimeout(() => {
            if (!botStoppedRef.current) {
              processNode(nextNode);
            }
          }, Math.min(delaySeconds * 1000, 2000)); // Max 2s no preview
          pendingTimeoutsRef.current.push(timeout);
        }
      }
    }
  };

  const addMessage = (type: 'bot' | 'user', content: string) => {
    console.log('💬 [addMessage] Called with:', { type, content });
    setMessages(prev => {
      // Verificar se a última mensagem é idêntica (evitar duplicação)
      const lastMessage = prev[prev.length - 1];
      if (lastMessage && lastMessage.type === type && lastMessage.content === content) {
        console.log('⚠️ [addMessage] Duplicate message detected, skipping:', content);
        return prev;
      }
      
      const newMessage = {
        id: Date.now().toString() + Math.random(), // Evitar duplicate keys
        type,
        content,
        timestamp: new Date(),
      };
      console.log('💬 [addMessage] Adding message:', newMessage);
      return [...prev, newMessage];
    });
  };

  const handleSendMessage = () => {
    if (!userInput.trim() || isProcessing) return;

    setIsProcessing(true);
    const input = userInput.trim().toLowerCase();
    addMessage('user', userInput.trim()); // Mostrar original (não lowercase)
    setUserInput('');

    // Verificar palavras de parada
    const triggerNode = nodes.find(n => n.data?.nodeType === 'trigger');
    const triggerConfig = triggerNode?.data?.config || {};
    const stopCommands = (triggerConfig.stopCommands || []).map((cmd: string) => cmd.toLowerCase().trim());

    if (stopCommands.some((cmd: string) => input === cmd || input.includes(cmd))) {
      // PARAR IMEDIATAMENTE - cancelar todos os processamentos pendentes
      console.log('🛑 [Preview] Stop command detected - stopping bot immediately');
      botStoppedRef.current = true;
      flowActiveRef.current = false;
      waitingForTriggerRef.current = false;
      
      // Cancelar todos os timeouts pendentes
      pendingTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      pendingTimeoutsRef.current = [];
      
      // Limpar nós processados
      processedNodesRef.current.clear();
      
      // Limpar tentativas de saveVariable
      saveVariableAttemptsRef.current = {};
      
      setTimeout(() => {
        addMessage('bot', '🛑 Bot encerrado por comando do usuário.');
        setIsProcessing(false);
      }, 300);
      return;
    }

    // Se está aguardando trigger (palavra-chave)
    if (waitingForTriggerRef.current && !flowActiveRef.current) {
      setTimeout(() => {
        if (checkTriggerMatch(input)) {
          // Palavra-chave correta - iniciar bot
          waitingForTriggerRef.current = false;
          flowActiveRef.current = true;
          startFlow();
        } else {
          // Palavra-chave incorreta - NÃO enviar fallback
          // Fallback só funciona DENTRO de uma sessão ativa
          // Se o bot não foi iniciado, simplesmente ignorar (silêncio)
          addMessage('bot', '⚠️ Palavra-chave não reconhecida. O bot não foi iniciado.');
        }
        setIsProcessing(false);
      }, 300);
      return;
    }

    // Processar resposta normal
    setTimeout(() => {
      processUserResponse(input);
      setIsProcessing(false);
    }, 300);
  };

  const checkTriggerMatch = (input: string): boolean => {
    const triggerNode = nodes.find(n => n.data?.nodeType === 'trigger');
    const triggerConfig = triggerNode?.data?.config || {};
    const triggerType = triggerConfig.triggerType || 'ANY_MESSAGE';
    const keywords = (triggerConfig.keywords || []).map((k: string) => k.toLowerCase().trim());

    if (triggerType === 'FIRST_MESSAGE') {
      // Primeira mensagem sempre inicia
      return true;
    } else if (triggerType === 'KEYWORD_EXACT') {
      // Verificar se a mensagem é exatamente igual a alguma palavra-chave
      return keywords.some((keyword: string) => input === keyword);
    } else if (triggerType === 'KEYWORD_CONTAINS') {
      // Verificar se contém a palavra-chave
      return keywords.some((keyword: string) => input.includes(keyword));
    } else if (triggerType === 'ANY_MESSAGE') {
      // Qualquer mensagem inicia
      return true;
    }

    return false;
  };

  const processUserResponse = (input: string) => {
    console.log('🎯 [Preview] Processing user response:', input);
    console.log('🎯 [Preview] Current node ID:', currentNodeId);

    if (!flowActiveRef.current) {
      return;
    }

    if (!currentNodeId) {
      addMessage('bot', '⚠️ Nenhum nó ativo.');
      return;
    }

    // Encontrar próximas edges do nó atual
    const nextEdges = edges.filter(e => e.source === currentNodeId);
    console.log('🎯 [Preview] Next edges:', nextEdges);

    if (nextEdges.length === 0) {
      addMessage('bot', '✅ Fim do fluxo.');
      flowActiveRef.current = false;
      return;
    }

    // Se tem condição ou saveVariable
    const currentNode = nodes.find(n => n.id === currentNodeId);
    console.log('🎯 [Preview] Current node:', currentNode);
    const isConditionNode = currentNode?.data?.nodeType === 'condition';
    const isSaveVariableNode = currentNode?.data?.nodeType === 'saveVariable';
    console.log('🎯 [Preview] Is condition node?', isConditionNode);
    console.log('🎯 [Preview] Is saveVariable node?', isSaveVariableNode);

    if (isSaveVariableNode) {
      // Processar saveVariable: validar e salvar
      const config = currentNode.data.config;
      const variableName = config?.variableName;
      const validationType = config?.validationType || 'none';
      const validationRegex = config?.validationRegex;
      const maxAttempts = config?.maxAttempts || 3;
      const onMaxAttemptsReached = config?.onMaxAttemptsReached || 'endFlow';

      if (!variableName) {
        addMessage('bot', '⚠️ Nó Salvar Variável não configurado corretamente.');
        return;
      }

      // Rastrear tentativas para este nó
      const attemptKey = currentNodeId || '';
      const currentAttempts = saveVariableAttemptsRef.current[attemptKey] || 0;

      // Validar se houver regex
      if (validationType !== 'none' && validationRegex) {
        try {
          const regex = new RegExp(validationRegex);
          const isValid = regex.test(input);

          if (!isValid) {
            // Incrementar tentativas
            const newAttempts = currentAttempts + 1;
            saveVariableAttemptsRef.current[attemptKey] = newAttempts;

            console.log(`❌ [Preview] Validation failed for "${variableName}" (attempt ${newAttempts}/${maxAttempts})`);

            // Verificar se excedeu máximo de tentativas
            if (newAttempts >= maxAttempts) {
              console.log(`⚠️ [Preview] Max attempts (${maxAttempts}) reached for "${variableName}"`);

              // Enviar mensagem de exceder tentativas se configurado
              if (config?.maxAttemptsMessage) {
                addMessage('bot', config.maxAttemptsMessage);
              }

              // Aplicar comportamento configurado
              if (onMaxAttemptsReached === 'endFlow') {
                console.log(`🛑 [Preview] Ending flow due to max attempts reached`);
                addMessage('bot', '🛑 Fluxo encerrado por exceder tentativas máximas.');
                flowActiveRef.current = false;
                return;
              } else if (onMaxAttemptsReached === 'continue') {
                console.log(`➡️ [Preview] Continuing flow despite validation failure (max attempts reached)`);
                // Limpar tentativas e continuar
                delete saveVariableAttemptsRef.current[attemptKey];
                // Avançar para próximo nó
                const nextEdge = edges.find(e => e.source === currentNodeId);
                if (nextEdge) {
                  const nextNode = nodes.find(n => n.id === nextEdge.target);
                  if (nextNode) {
                    setTimeout(() => processNode(nextNode), 500);
                    return;
                  }
                }
                addMessage('bot', '✅ Continuando fluxo. Fim do fluxo.');
                flowActiveRef.current = false;
                return;
              }
            } else {
              // Ainda há tentativas disponíveis - enviar mensagem de erro ou repetição
              let messageToSend: string;
              if (currentAttempts === 0) {
                // Primeira tentativa - usar mensagem de erro
                messageToSend = config?.errorMessage || 
                  'Valor inválido. Por favor, tente novamente.';
                console.log(`📤 [Preview] Sending error message (first attempt): "${messageToSend}"`);
              } else {
                // Tentativas seguintes - usar mensagem de repetição
                messageToSend = config?.retryMessage || 
                  config?.errorMessage || 
                  `Por favor, digite novamente. O formato informado não está correto. (Tentativa ${newAttempts}/${maxAttempts})`;
                console.log(`📤 [Preview] Sending retry message (attempt ${newAttempts}): "${messageToSend}"`);
              }
              
              addMessage('bot', messageToSend);
              return; // Aguardar nova resposta
            }
          }

          console.log(`✅ [Preview] Validation passed for "${variableName}"`);
        } catch (error) {
          console.error('Invalid regex:', validationRegex);
        }
      }

      // Se chegou aqui, validação passou ou não havia validação
      // Limpar tentativas (sucesso)
      delete saveVariableAttemptsRef.current[attemptKey];

      // Salvar variável (simulado - em produção seria salvo na sessão)
      console.log(`💾 [Preview] Variable "${variableName}" saved: "${input}"`);

      // Avançar para próximo nó
      const nextEdge = edges.find(e => e.source === currentNodeId);
      if (nextEdge) {
        const nextNode = nodes.find(n => n.id === nextEdge.target);
        if (nextNode) {
          // Se próximo nó é saveVariable, atualizar currentNodeId e enviar prompt
          if (nextNode.data?.nodeType === 'saveVariable') {
            setCurrentNodeId(nextNode.id);
            const promptMessage = nextNode.data.config?.promptMessage || 'Por favor, digite a informação solicitada:';
            setTimeout(() => {
              addMessage('bot', promptMessage);
            }, 500);
            return;
          }
          // Para outros tipos de nós, processar normalmente
          setTimeout(() => processNode(nextNode), 500);
          return;
        }
      }

      addMessage('bot', '✅ Variável salva. Fim do fluxo.');
      flowActiveRef.current = false;
      return;
    }

    if (isConditionNode) {
      const config = currentNode.data.config;
      const normalizedInput = input.toLowerCase().trim();
      const isSwitchMode = config?.mode === 'switch' && Array.isArray(config?.cases);

      let targetEdge: typeof nextEdges[number] | undefined;

      if (isSwitchMode) {
        const cases: any[] = config.cases || [];
        console.log(`🔀 [Preview] Switch mode com ${cases.length} cases. Input: "${normalizedInput}"`);

        let matchedIndex = -1;
        for (let i = 0; i < cases.length; i++) {
          const caseConfig = cases[i];
          const caseValue = (caseConfig?.value ?? '').toString().toLowerCase().trim();
          const caseConditionType = caseConfig?.conditionType || 'equals';
          let matches = false;

          switch (caseConditionType) {
            case 'equals':
              matches = normalizedInput === caseValue;
              break;
            case 'contains':
              matches = caseValue !== '' && normalizedInput.includes(caseValue);
              break;
            case 'startsWith':
              matches = caseValue !== '' && normalizedInput.startsWith(caseValue);
              break;
            case 'endsWith':
              matches = caseValue !== '' && normalizedInput.endsWith(caseValue);
              break;
            case 'notEquals':
              matches = normalizedInput !== caseValue;
              break;
            case 'regex':
              try {
                matches = new RegExp(caseConfig.value, 'i').test(normalizedInput);
              } catch (err) {
                console.error(`❌ [Preview] Regex inválida no case ${i}:`, caseConfig.value, err);
                matches = false;
              }
              break;
            default:
              matches = normalizedInput === caseValue;
          }

          if (matches) {
            matchedIndex = i;
            console.log(`✅ [Preview] Case ${i} matched: label="${caseConfig.label}" value="${caseConfig.value}"`);
            break;
          }
        }

        if (matchedIndex >= 0) {
          const caseHandle = `case-${matchedIndex}`;
          targetEdge = nextEdges.find(e => e.sourceHandle === caseHandle);
          if (!targetEdge) {
            console.warn(`⚠️ [Preview] Nenhuma edge com sourceHandle="${caseHandle}", tentando default`);
            targetEdge = nextEdges.find(e => e.sourceHandle === 'default');
          }
        } else {
          console.log(`❌ [Preview] Nenhum case correspondeu, usando edge default`);
          targetEdge = nextEdges.find(e => e.sourceHandle === 'default');
        }
      } else {
        // Modo simples (true/false)
        const operator = config?.conditionType || config?.operator || 'equals';
        const value = config?.value?.toLowerCase() || '';

        console.log('🔍 [Preview] Evaluating condition:', { operator, value, input: normalizedInput, config });

        let conditionMet = false;

        switch (operator) {
          case 'equals':
          case '==':
            conditionMet = normalizedInput === value;
            break;
          case 'contains':
            conditionMet = normalizedInput.includes(value);
            break;
          case 'startsWith':
            conditionMet = normalizedInput.startsWith(value);
            break;
          case 'endsWith':
            conditionMet = normalizedInput.endsWith(value);
            break;
          case 'notEquals':
          case '!=':
            conditionMet = normalizedInput !== value;
            break;
          default:
            console.warn('⚠️ Unknown operator, defaulting to contains:', operator);
            conditionMet = normalizedInput.includes(value);
        }

        console.log('📊 [Preview] Condition result:', conditionMet);
        console.log('🔀 [Preview] Available edges:', nextEdges.map(e => ({ label: e.label, sourceHandle: e.sourceHandle })));

        // Encontrar edge usando sourceHandle (mais confiável)
        targetEdge = nextEdges.find(e => {
          const handle = e.sourceHandle?.toLowerCase();
          if (conditionMet) {
            return handle === 'true' || handle === 'yes' || handle === 'sim';
          } else {
            return handle === 'false' || handle === 'no' || handle === 'não' || handle === 'nao';
          }
        });

        // Fallback: tentar via label
        if (!targetEdge) {
          targetEdge = nextEdges.find(e => {
            const label = e.label?.toString().toLowerCase();
            return conditionMet
              ? (label === 'true' || label === 'sim' || label === 'yes' || label === 'verdadeiro')
              : (label === 'false' || label === 'não' || label === 'no' || label === 'falso' || label === 'nao');
          });
        }
      }

      console.log('✅ [Preview] Selected edge:', targetEdge ? { label: targetEdge.label, sourceHandle: targetEdge.sourceHandle } : 'NENHUMA');

      if (targetEdge) {
        const nextNode = nodes.find(n => n.id === targetEdge.target);
        if (nextNode) {
          const timeout = setTimeout(() => {
            if (!botStoppedRef.current) {
              processNode(nextNode);
            }
          }, 500);
          pendingTimeoutsRef.current.push(timeout);
          return;
        }
      }

      // Sem edge válida: finalizar (NÃO usar fallback para nextEdges[0] em switch — geraria match incorreto)
      if (flowActiveRef.current && !botStoppedRef.current) {
        console.log('⚠️ [Preview] No valid edge found for condition, ending flow');
        addMessage('bot', '⚠️ Nenhuma condição correspondeu e não há saída padrão.');
        flowActiveRef.current = false;
        return;
      }
    } else {
      // Não é condição, seguir para próximo nó
      if (nextEdges.length > 0) {
        const nextNode = nodes.find(n => n.id === nextEdges[0].target);
        if (nextNode) {
          const timeout = setTimeout(() => {
            if (!botStoppedRef.current) {
              processNode(nextNode);
            }
          }, 500);
          pendingTimeoutsRef.current.push(timeout);
          return;
        }
      }
    }

    if (!botStoppedRef.current) {
      addMessage('bot', '✅ Fim do fluxo.');
      flowActiveRef.current = false;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setUserInput(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  return (
    <aside className="w-96 bg-white border-l border-gray-200 flex flex-col shadow-2xl z-40">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <span className="text-2xl">🤖</span>
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Preview do Bot</h2>
            <p className="text-xs text-purple-100">Teste o fluxo antes de publicar</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 mt-10">
            <p className="text-sm">Iniciando conversa...</p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                message.type === 'user'
                  ? 'bg-purple-600 text-white rounded-br-none'
                  : 'bg-white text-gray-800 shadow-sm rounded-bl-none border border-gray-200'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              <p className={`text-xs mt-1 ${message.type === 'user' ? 'text-purple-100' : 'text-gray-400'}`}>
                {message.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white relative">
        {/* Emoji Picker */}
        {showEmojiPicker && (
          <div ref={emojiPickerRef} className="absolute bottom-20 right-4 z-50">
            <EmojiPicker onEmojiClick={handleEmojiClick} />
          </div>
        )}

        <div className="flex space-x-2">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="px-3 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors"
            title="Adicionar emoji"
          >
            <span className="text-xl">😀</span>
          </button>
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={waitingForTriggerRef.current && !flowActiveRef.current ? "Digite uma palavra-chave para iniciar..." : "Digite sua mensagem..."}
            disabled={isProcessing || (!flowActiveRef.current && !waitingForTriggerRef.current)}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 text-sm"
          />
          <button
            onClick={handleSendMessage}
            disabled={!userInput.trim() || isProcessing || (!flowActiveRef.current && !waitingForTriggerRef.current)}
            className="px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Pressione Enter para enviar
        </p>
      </div>
    </aside>
  );
}
